import { useEffect, useState } from 'react'
import {
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  TextInput,
  Text,
  Alert,
  Loader,
  Center,
  CopyButton,
  Code,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconTrash, IconCopy, IconCheck, IconKey } from '@tabler/icons-react'
import { ApiKey, NewApiKey, listApiKeys, createApiKey, revokeApiKey, getErrorMessage } from '../../api/apiKeysApi'

interface ApiKeysSectionProps {
  appId: string
}

export function ApiKeysSection({ appId }: ApiKeysSectionProps) {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [newKeyName, setNewKeyName] = useState('')
  const [isIssuing, setIsIssuing] = useState(false)
  const [nameError, setNameError] = useState<string | undefined>()
  const [issuedKey, setIssuedKey] = useState<NewApiKey | null>(null)

  useEffect(() => {
    loadKeys()
  }, [appId])

  async function loadKeys() {
    setIsLoading(true)
    try {
      const data = await listApiKeys(appId)
      setKeys(data)
    } catch (err) {
      notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleIssue() {
    const trimmed = newKeyName.trim()
    if (!trimmed) {
      setNameError('Name is required')
      return
    }
    if (trimmed.length > 100) {
      setNameError('Name must be 100 characters or less')
      return
    }
    setNameError(undefined)
    setIsIssuing(true)
    try {
      const created = await createApiKey(appId, trimmed)
      setIssuedKey(created)
      setNewKeyName('')
      setKeys((prev) => [created, ...prev])
    } catch (err) {
      notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
    } finally {
      setIsIssuing(false)
    }
  }

  function handleRevoke(key: ApiKey) {
    modals.openConfirmModal({
      title: 'Revoke API Key',
      children: (
        <Text size="sm">
          Are you sure you want to revoke <strong>{key.name}</strong>? Any services using this key will immediately lose access.
        </Text>
      ),
      labels: { confirm: 'Revoke', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revokeApiKey(appId, key._id)
          setKeys((prev) => prev.map((k) => k._id === key._id ? { ...k, isActive: false } : k))
          if (issuedKey?._id === key._id) setIssuedKey(null)
          notifications.show({ title: 'Revoked', message: `"${key.name}" has been revoked`, color: 'green' })
        } catch (err) {
          notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
        }
      },
    })
  }

  const activeKeys = keys.filter((k) => k.isActive)
  const revokedKeys = keys.filter((k) => !k.isActive)
  const displayKeys = [...activeKeys, ...revokedKeys]

  return (
    <Stack gap="md">
      {issuedKey && (
        <Alert
          icon={<IconKey size={16} />}
          title="API key created — copy it now"
          color="yellow"
          withCloseButton
          onClose={() => setIssuedKey(null)}
        >
          <Text size="sm" mb="xs">
            This key will not be shown again.
          </Text>
          <Group gap="xs" align="center">
            <Code style={{ flex: 1, wordBreak: 'break-all' }}>{issuedKey.key}</Code>
            <CopyButton value={issuedKey.key}>
              {({ copied, copy }) => (
                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              )}
            </CopyButton>
          </Group>
        </Alert>
      )}

      <Group gap="sm" align="flex-end">
        <TextInput
          label="New key name"
          placeholder="e.g. flight-plan-sync"
          value={newKeyName}
          onChange={(e) => { setNewKeyName(e.target.value); setNameError(undefined) }}
          error={nameError}
          style={{ flex: 1 }}
          maxLength={100}
        />
        <Button
          leftSection={<IconKey size={16} />}
          onClick={handleIssue}
          loading={isIssuing}
          mb={nameError ? 20 : 0}
        >
          Issue Key
        </Button>
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : displayKeys.length === 0 ? (
        <Text c="dimmed" ta="center" py="md" size="sm">
          No API keys yet.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Last used</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayKeys.map((key) => (
              <Table.Tr key={key._id}>
                <Table.Td>
                  <Text size="sm">{key.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={key.isActive ? 'green' : 'gray'} variant="light">
                    {key.isActive ? 'Active' : 'Revoked'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {new Date(key.createdAt).toLocaleDateString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {key.isActive && (
                    <ActionIcon variant="subtle" color="red" onClick={() => handleRevoke(key)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
